import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { PROFILE_VERTICAL_CONFIGS, type OutcomeMetric } from '@photofeeler/shared';
import { useAuth } from '@/contexts/AuthContext';
import { useUserStore } from '@/stores/user';
import { useGameStore } from '@/stores/game';
import { API_CONFIG } from '@/config';
import { getLevelDef, getLevelProgress } from '@/data/levels';
import { buildReturnPrompts } from '@/data/project-loops';

interface PhotoAnalysis {
  id: string;
  context: string;
  status: string;
  aggregates: { composite_score: number; model_count: number }[];
}

const OUTCOME_METRICS: Record<'dating' | 'linkedin', { value: OutcomeMetric; label: string }[]> = {
  dating: [
    { value: 'matches', label: 'Matches' },
    { value: 'quality_matches', label: 'Quality matches' },
    { value: 'replies', label: 'Replies' },
  ],
  linkedin: [
    { value: 'profile_views', label: 'Profile views' },
    { value: 'recruiter_messages', label: 'Recruiter messages' },
    { value: 'connection_acceptance', label: 'Connection acceptance' },
  ],
};

export function MeTab() {
  const navigate = useNavigate();
  const { user, session, signOut } = useAuth();
  const { credits } = useUserStore();
  const {
    xp,
    level,
    streakDays,
    projects,
    activeProjectId,
    setActiveProject,
    outcomeLogs,
    retestLogs,
    logOutcome,
  } = useGameStore();
  const [selectedProjectId, setSelectedProjectId] = useState(activeProjectId);
  const [selectedMetric, setSelectedMetric] = useState<OutcomeMetric>('matches');
  const [resultValue, setResultValue] = useState('');
  const [note, setNote] = useState('');

  useEffect(() => {
    setSelectedProjectId(activeProjectId);
  }, [activeProjectId]);

  const selectedProject =
    projects.find(project => project.id === selectedProjectId) ||
    projects.find(project => project.id === activeProjectId) ||
    projects[0];

  useEffect(() => {
    if (!selectedProject) return;
    setSelectedMetric(OUTCOME_METRICS[selectedProject.vertical][0].value);
  }, [selectedProject?.id, selectedProject?.vertical]);

  const levelDef = getLevelDef(level);
  const nextLevelDef = getLevelDef(Math.min(level + 1, 10));
  const progress = getLevelProgress(xp);

  const { data } = useQuery({
    queryKey: ['photos', user?.id],
    queryFn: async () => {
      if (!session?.access_token) return { photos: [] };
      const res = await fetch(`${API_CONFIG.RATE_URL}/photos`, {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      if (!res.ok) return { photos: [] };
      return res.json() as Promise<{ photos: PhotoAnalysis[] }>;
    },
    enabled: !!session?.access_token,
  });

  const photos = data?.photos || [];
  const completedPhotos = photos.filter(photo => photo.status === 'completed');
  const scores = completedPhotos
    .map(photo => photo.aggregates?.[0]?.composite_score)
    .filter((score): score is number => score != null);
  const photoIQ = scores.length > 0
    ? Math.round(scores.reduce((sum, score) => sum + score, 0) / scores.length)
    : null;

  const activityFeed = useMemo(() => {
    const retestItems = retestLogs.map(log => ({
      id: log.id,
      date: log.loggedAt,
      title: 'Retest recorded',
      detail:
        log.change == null
          ? `New score ${log.score}`
          : `Score moved by ${log.change >= 0 ? '+' : ''}${log.change} to ${log.score}`,
    }));
    const outcomeItems = outcomeLogs.map(log => ({
      id: log.id,
      date: log.loggedAt,
      title: log.metric.replace('_', ' '),
      detail: `${log.value}${log.note ? ` · ${log.note}` : ''}`,
    }));

    return [...retestItems, ...outcomeItems]
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
      .slice(0, 8);
  }, [outcomeLogs, retestLogs]);
  const returnPrompts = useMemo(() => buildReturnPrompts(projects, outcomeLogs), [projects, outcomeLogs]);

  const handleLogResult = () => {
    if (!selectedProject) return;
    const value = parseInt(resultValue, 10);
    if (Number.isNaN(value) || value < 0) return;

    logOutcome(selectedProject.id, selectedMetric, value, note || undefined);
    setResultValue('');
    setNote('');
  };

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
  };

  return (
    <div className="pb-32 pt-8 px-4 sm:px-6 max-w-6xl mx-auto animate-fade-in">
      <div className="mb-6">
        <h1 className="font-sans text-xl text-ink mb-1" style={{ fontWeight: 600 }}>
          Track what is getting better
        </h1>
        <p className="text-sm text-ink-faint font-sans" style={{ fontWeight: 300 }}>
          See recent scores, retests, compare history, and the outcomes that matter.
        </p>
      </div>
      <div className="marketing-panel-warm p-6 sm:p-8 mb-8">
        <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
          <div className="text-center lg:text-left">
        <div className="w-16 h-16 bg-cream-warm rounded-full flex items-center justify-center text-2xl mx-auto lg:mx-0 mb-3">
          {levelDef.emoji}
        </div>
        <div className="section-kicker !text-center lg:!text-left !mb-3">Progress</div>
        <h2 className="font-serif text-4xl text-ink mb-1" style={{ fontWeight: 600 }}>
          {user?.email?.split('@')[0] || 'User'}
        </h2>
        <p className="text-xs text-ink-faint font-sans" style={{ fontWeight: 300 }}>
          {user?.email}
        </p>
        {photoIQ != null && (
          <div className="mt-3 inline-flex items-center gap-1.5 px-3 py-1 bg-rose-faint rounded-full border border-rose/15">
            <span className="text-xs font-sans font-medium text-rose">Photo IQ: {photoIQ}</span>
          </div>
        )}
          </div>

          <div className="grid grid-cols-3 gap-3">
            {[
              { label: 'Projects', value: projects.length },
              { label: 'Streak', value: `${streakDays}d` },
              { label: 'Credits', value: credits },
            ].map(stat => (
              <div key={stat.label} className="metric-tile text-center min-w-[110px]">
                <div className="font-serif text-3xl text-ink" style={{ fontWeight: 600 }}>
                  {stat.value}
                </div>
                <div className="text-[10px] text-ink-faint font-sans uppercase tracking-[0.18em] mt-1">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="card p-5 mb-5">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-sans font-medium text-ink">
            Level {level}: {levelDef.name}
          </span>
          <span className="text-xs text-ink-faint" style={{ fontWeight: 300 }}>
            {xp} / {nextLevelDef.xpRequired} XP
          </span>
        </div>
        <div className="w-full h-2 bg-surface-border rounded-full overflow-hidden">
          <div
            className="h-full bg-success rounded-full transition-all duration-500"
            style={{ width: `${Math.min(progress * 100, 100)}%` }}
          />
        </div>
        <p className="text-[11px] text-ink-faint mt-1.5" style={{ fontWeight: 300 }}>
          {nextLevelDef.xpRequired - xp > 0
            ? `${nextLevelDef.xpRequired - xp} XP to Level ${level + 1}`
            : 'Max level reached!'}
        </p>
      </div>

      <div className="mb-6">
        <h2 className="text-sm font-sans font-medium text-ink-muted mb-3">
          Progress by workspace
        </h2>
        <div className="space-y-3">
          {projects.map(project => {
            const config = PROFILE_VERTICAL_CONFIGS[project.vertical];
            const projectOutcomeCount = outcomeLogs.filter(log => log.projectId === project.id).length;
            const projectRetestCount = retestLogs.filter(log => log.projectId === project.id).length;

            return (
              <button
                key={project.id}
                onClick={() => {
                  setSelectedProjectId(project.id);
                  setActiveProject(project.id);
                }}
                className={`card-hover w-full text-left p-5 ${selectedProject?.id === project.id ? 'ring-1 ring-rose/40 bg-rose-faint' : ''}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="font-serif text-2xl text-ink">
                      {config.emoji} {project.title}
                    </div>
                    <div className="text-xs text-ink-faint mt-1" style={{ fontWeight: 300 }}>
                      {project.goal}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-xl font-sans text-ink" style={{ fontWeight: 600 }}>
                      {project.currentScore ?? '--'}
                    </div>
                    <div className="text-[10px] text-ink-faint">current score</div>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-2 mt-4 text-center">
                  {[
                    { label: 'Photos', value: project.photoIds.length },
                    { label: 'Retests', value: projectRetestCount },
                    { label: 'Outcomes', value: projectOutcomeCount },
                  ].map(stat => (
                    <div key={stat.label} className="rounded-xl border border-surface-border bg-surface px-2 py-2">
                      <div className="text-sm font-sans text-ink" style={{ fontWeight: 600 }}>
                        {stat.value}
                      </div>
                      <div className="text-[10px] text-ink-faint">{stat.label}</div>
                    </div>
                  ))}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {returnPrompts.length > 0 && (
        <div className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-sans font-medium text-ink-muted">
              Return prompts
            </h2>
            <span className="text-[11px] text-ink-faint">
              Follow up after audits and rewrites
            </span>
          </div>
          <div className="space-y-3">
            {returnPrompts.map(prompt => (
              <div key={prompt.id} className="marketing-panel p-4">
                <div className="flex items-start justify-between gap-4 mb-2">
                  <div>
                    <div className="font-serif text-2xl text-ink">{prompt.title}</div>
                    <div className="text-sm text-ink-muted mt-2" style={{ fontWeight: 300 }}>
                      {prompt.detail}
                    </div>
                  </div>
                  <div className="hero-chip py-1.5 px-3 text-[10px] uppercase tracking-[0.22em]">
                    {prompt.source}
                  </div>
                </div>
                <button
                  onClick={() => {
                    setSelectedProjectId(prompt.projectId);
                    setActiveProject(prompt.projectId);
                    setSelectedMetric(prompt.metric);
                  }}
                  className="btn-secondary text-[11px]"
                >
                  {prompt.ctaLabel}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {selectedProject && (
        <div className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-sans font-medium text-ink-muted">
              Log outcomes
            </h2>
            <span className="text-[11px] text-ink-faint">
              {selectedProject.title}
            </span>
          </div>
          <div className="card p-4 space-y-3">
            <select
              value={selectedProjectId}
              onChange={e => {
                setSelectedProjectId(e.target.value);
                setActiveProject(e.target.value);
              }}
              className="input text-sm"
            >
              {projects.map(project => (
                <option key={project.id} value={project.id}>
                  {project.title}
                </option>
              ))}
            </select>

            <select
              value={selectedMetric}
              onChange={e => setSelectedMetric(e.target.value as OutcomeMetric)}
              className="input text-sm"
            >
              {OUTCOME_METRICS[selectedProject.vertical].map(metric => (
                <option key={metric.value} value={metric.value}>
                  {metric.label}
                </option>
              ))}
            </select>

            <input
              type="number"
              min="0"
              value={resultValue}
              onChange={e => setResultValue(e.target.value)}
              placeholder="Result value"
              className="input text-sm"
            />

            <input
              type="text"
              value={note}
              onChange={e => setNote(e.target.value)}
              placeholder="Optional note"
              className="input text-sm"
            />

            <button onClick={handleLogResult} className="btn-rose text-xs px-4 py-2.5">
              Save progress log
            </button>
          </div>
        </div>
      )}

      <div className="mb-6">
        <h2 className="text-sm font-sans font-medium text-ink-muted mb-3">
          Recent activity
        </h2>
        {activityFeed.length > 0 ? (
          <div className="card p-4 space-y-3">
            {activityFeed.map(item => (
              <div key={item.id} className="flex items-start justify-between gap-4">
                <div>
                  <div className="text-sm font-sans text-ink" style={{ fontWeight: 500 }}>
                    {item.title}
                  </div>
                  <div className="text-xs text-ink-faint mt-0.5" style={{ fontWeight: 300 }}>
                    {item.detail}
                  </div>
                </div>
                <span className="text-[10px] text-ink-faint whitespace-nowrap">
                  {new Date(item.date).toLocaleDateString()}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <div className="card text-center py-6">
            <p className="text-xs text-ink-faint" style={{ fontWeight: 300 }}>
              No activity yet. Run a retest or log outcomes after you update a profile.
            </p>
          </div>
        )}
      </div>

      <div className="mb-6">
        <h2 className="text-sm font-sans font-medium text-ink-muted mb-3">
          Subscription
        </h2>
        <div className="card p-5">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-sm font-sans font-medium text-ink">Free Plan</div>
              <div className="text-xs text-ink-faint" style={{ fontWeight: 300 }}>
                {credits} credit{credits !== 1 ? 's' : ''} remaining
              </div>
            </div>
            <button onClick={() => navigate('/purchase')} className="btn-rose text-xs px-4 py-2">
              Upgrade
            </button>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center gap-4 mb-4">
        <button
          onClick={handleSignOut}
          className="text-xs font-sans text-ink-faint hover:text-ink transition-colors"
        >
          Log out
        </button>
        <div className="w-px h-3 bg-surface-border" />
        <button
          onClick={() => navigate('/privacy')}
          className="text-xs font-sans text-ink-faint hover:text-ink transition-colors"
        >
          Privacy
        </button>
        <div className="w-px h-3 bg-surface-border" />
        <button
          onClick={() => navigate('/terms')}
          className="text-xs font-sans text-ink-faint hover:text-ink transition-colors"
        >
          Terms
        </button>
      </div>
    </div>
  );
}
